/** Reporting engine — the draggable / resizable / lockable widget grid.
 *
 * Ported from the dashboard mockup. A 12-column grid; in edit mode a widget's
 * header is a drag handle (move) and the corner is a resize handle, and lock
 * pins a widget in place. `persist` only notifies the parent. The save itself
 * is the parent's debounced effect on the widgets state, not this callback.
 * In view mode a widget is a button that opens the focus explorer.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  CSSProperties,
  Dispatch,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  SetStateAction,
} from "react";

import type { ReportingTicketRow } from "../services/api";

import { C, MONO, SANS, countActiveFilters } from "./data";
import type { Drill, Widget } from "./types";
import { WidgetBody } from "./widgets";

type Row = ReportingTicketRow;

export const GRID = { cols: 12, gap: 16, rowH: 88 };

/** Columns never draw narrower than this, so the absolute grid is at least
 *  12×40 + 11×16 = 656px. Below that the board stacks instead of widening
 *  the page (issue #266). */
const COL_FLOOR = 40;

export function defaultWidgets(): Widget[] {
  return [
    { id: "w_open", type: "kpi", metric: "opened", title: "Tickets opened", filters: {}, pos: { x: 0, y: 0, w: 3, h: 2 }, locked: false },
    { id: "w_close", type: "kpi", metric: "closed", title: "Tickets closed", filters: {}, pos: { x: 3, y: 0, w: 3, h: 2 }, locked: false },
    { id: "w_resp", type: "kpi", metric: "avgResponse", title: "Avg first response", filters: {}, pos: { x: 6, y: 0, w: 3, h: 2 }, locked: false },
    { id: "w_csat", type: "kpi", metric: "csat", title: "Customer satisfaction", filters: {}, pos: { x: 9, y: 0, w: 3, h: 2 }, locked: false },
    { id: "w_line", type: "line", title: "The shape of the month", filters: {}, pos: { x: 0, y: 2, w: 8, h: 4 }, locked: false },
    { id: "w_sla", type: "ring", metric: "sla", title: "Answered on time, this month", filters: {}, pos: { x: 8, y: 2, w: 4, h: 2 }, locked: false },
    { id: "w_back", type: "ring", metric: "backlog", title: "Open backlog", filters: {}, pos: { x: 8, y: 4, w: 4, h: 2 }, locked: false },
    { id: "w_prio", type: "bar", dimension: "priority", title: "By priority", filters: {}, pos: { x: 0, y: 6, w: 4, h: 4 }, locked: false },
    { id: "w_issue", type: "bar", dimension: "issueType", title: "By issue type", filters: {}, pos: { x: 4, y: 6, w: 4, h: 4 }, locked: false },
    { id: "w_lead", type: "leaderboard", board: "contact", title: "Top requesters", filters: {}, pos: { x: 8, y: 6, w: 4, h: 4 }, locked: false },
    { id: "w_table", type: "table", title: "Recent tickets", filters: {}, pos: { x: 0, y: 10, w: 12, h: 5 }, locked: false },
    { id: "w_csatmix", type: "csat", title: "Satisfaction ratings", filters: {}, pos: { x: 0, y: 15, w: 4, h: 3 }, locked: false },
  ];
}

const styles: Record<string, CSSProperties> = {
  card: {
    height: "100%",
    background: "#fff",
    border: `1px solid ${C.rule}`,
    borderRadius: 3,
    boxShadow: "0 1px 2px rgba(20,20,20,0.04), 0 4px 14px rgba(20,20,20,0.04)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  cardHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
    padding: "12px 14px 10px",
    borderBottom: `1px solid ${C.rule}`,
  },
  cardTitle: {
    fontFamily: SANS,
    fontWeight: 600,
    fontSize: 12.5,
    color: C.ink,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardSub: { fontSize: 9.5, color: C.stone, fontFamily: MONO, marginTop: 3, letterSpacing: "0.03em" },
  cardBody: { flex: 1, minHeight: 0, padding: "12px 14px", overflow: "hidden" },
  openHint: { fontFamily: MONO, fontSize: 9.5, color: C.stone, opacity: 0, transition: "opacity .15s", whiteSpace: "nowrap", flex: "0 0 auto" },
  resizeHandle: { position: "absolute", right: 2, bottom: 2, width: 20, height: 20, display: "flex", alignItems: "flex-end", justifyContent: "flex-end", cursor: "nwse-resize", padding: 2 },
  lockBadge: { position: "absolute", left: 8, bottom: 6, fontFamily: MONO, fontSize: 9, color: C.stone, letterSpacing: "0.1em", textTransform: "uppercase" },
};

/** Edit-mode-only note that a widget carries saved filters.
 *
 * Every widget used to caption itself with its own type ("KPI", "Gauge",
 * "Trend · opened vs closed"), which read as noise on a client's dashboard —
 * the widget's shape already says what it is (issue #89). The saved-filter
 * count is the one thing the card cannot show, and it only matters while
 * authoring, so it survives in edit mode alone. */
function filterNote(w: Widget): string | null {
  const fc = countActiveFilters(w.filters);
  return fc > 0 ? `${fc} filter${fc > 1 ? "s" : ""}` : null;
}

function IconBtn({
  title,
  onClick,
  children,
  active,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      // The header's drag handler listens on pointerdown, which fires before
      // mousedown and is not stopped by it — without this, clicking an icon
      // starts a widget drag and any pointer drift relocates the widget.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        font: "inherit",
        cursor: "pointer",
        width: 26,
        height: 26,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${active ? C.forest : C.rule}`,
        background: active ? "#f0f6ef" : "#fff",
        color: active ? C.forest : C.mud,
        borderRadius: 2,
        fontSize: 13,
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}

interface Geo {
  cellX: (x: number) => number;
  cellY: (y: number) => number;
  wPx: (w: number) => number;
  hPx: (h: number) => number;
  dragging: string | null;
}

type MutateFn = (w: Widget) => Widget;

function WidgetFrame({
  widget,
  geo,
  editMode,
  tickets,
  prevTickets,
  days,
  onOpen,
  onEdit,
  onDrag,
  onResize,
  onMutate,
  stacked,
}: {
  widget: Widget;
  geo: Geo;
  editMode: boolean;
  tickets: Row[];
  prevTickets: Row[] | null;
  days: number;
  onOpen: (w: Widget, drill?: Drill) => void;
  onEdit: (w: Widget) => void;
  onDrag: (e: ReactPointerEvent, w: Widget) => void;
  onResize: (e: ReactPointerEvent, w: Widget) => void;
  onMutate: (id: string, fn: MutateFn | "del" | "dup") => void;
  stacked: boolean;
}) {
  const p = widget.pos;
  const interactive = widget.interactive ?? true;
  // A display-only widget is inert for everyone, staff included, so the editor
  // sees exactly what customers see instead of having to view-as a client.
  const clickable = !editMode && interactive;
  const big =
    widget.type === "line" || ((widget.type === "ring" || widget.type === "csat") && widget.pos.h >= 3);
  return (
    <div
      style={
        stacked
          ? {
              position: "relative",
              width: "100%",
              minWidth: 0,
              height: geo.hPx(p.h),
            }
          : {
              position: "absolute",
              left: geo.cellX(p.x),
              top: geo.cellY(p.y),
              width: geo.wPx(p.w),
              height: geo.hPx(p.h),
              transition: geo.dragging === widget.id ? "none" : "left .16s, top .16s, width .16s, height .16s",
            }
      }
    >
      <div
        onClick={clickable ? () => onOpen(widget) : undefined}
        className={clickable ? "rp-card-hover" : ""}
        style={{
          ...styles.card,
          cursor: clickable ? "pointer" : "default",
          outline: editMode ? `1px dashed ${widget.locked ? C.stone : C.forest}` : "none",
          outlineOffset: 2,
        }}
      >
        <div
          style={{ ...styles.cardHead, cursor: editMode && !widget.locked && !stacked ? "grab" : "default" }}
          onPointerDown={editMode && !widget.locked && !stacked ? (e) => onDrag(e, widget) : undefined}
        >
          <div style={{ minWidth: 0 }}>
            <div style={styles.cardTitle}>{widget.title}</div>
            {editMode && filterNote(widget) && (
              <div style={styles.cardSub}>{filterNote(widget)}</div>
            )}
          </div>
          {editMode ? (
            <div style={{ display: "flex", gap: 5, flex: "0 0 auto" }}>
              <IconBtn title="Edit widget — type, metric and filters" onClick={() => onEdit(widget)}>
                ✎
              </IconBtn>
              <IconBtn
                title={widget.locked ? "Unlock" : "Lock position"}
                active={widget.locked}
                onClick={() => onMutate(widget.id, (w) => ({ ...w, locked: !w.locked }))}
              >
                {widget.locked ? "🔒" : "🔓"}
              </IconBtn>
              <IconBtn
                title={
                  interactive
                    ? "Drill-down is on for this widget — click to make it display-only"
                    : "Display-only — click to turn drill-down back on"
                }
                active={interactive}
                onClick={() => onMutate(widget.id, (w) => ({ ...w, interactive: !(w.interactive ?? true) }))}
              >
                ⤢
              </IconBtn>
              <IconBtn title="Duplicate" onClick={() => onMutate(widget.id, "dup")}>
                ⧉
              </IconBtn>
              <IconBtn title="Remove" onClick={() => onMutate(widget.id, "del")}>
                ✕
              </IconBtn>
            </div>
          ) : clickable ? (
            <span className="rp-open-hint" style={styles.openHint}>
              ⤢ open & drill
            </span>
          ) : null}
        </div>
        <div style={styles.cardBody}>
          {/* onDrill is handed over only when this widget can be opened, so a
              display-only breakdown renders inert rows rather than offering a
              click that goes nowhere (issue #117). */}
          <WidgetBody
            widget={widget}
            tickets={tickets}
            prevTickets={prevTickets}
            days={days}
            big={big}
            onDrill={clickable ? (drill) => onOpen(widget, drill) : undefined}
          />
        </div>
        {editMode && !widget.locked && !stacked && (
          <div onPointerDown={(e) => onResize(e, widget)} style={styles.resizeHandle} title="Resize">
            <svg width="14" height="14" viewBox="0 0 14 14">
              <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" stroke={C.stone} strokeWidth="1.2" />
            </svg>
          </div>
        )}
        {widget.locked && editMode && <div style={styles.lockBadge}>locked</div>}
      </div>
    </div>
  );
}

interface DragState {
  id: string;
  mode: "move" | "resize";
  sx: number;
  sy: number;
  orig: Widget["pos"];
}

export function Board({
  widgets,
  setWidgets,
  editMode,
  tickets,
  prevTickets,
  days,
  onOpen,
  onEdit,
  persist,
}: {
  widgets: Widget[];
  setWidgets: Dispatch<SetStateAction<Widget[]>>;
  editMode: boolean;
  tickets: Row[];
  prevTickets: Row[] | null;
  days: number;
  onOpen: (w: Widget, drill?: Drill) => void;
  onEdit: (w: Widget) => void;
  persist: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // 0 until measured. useLayoutEffect writes the real width before paint, so
  // there is no stacked-then-grid flash and no viewport guess.
  const [cw, setCw] = useState(0);
  const [drag, setDrag] = useState<DragState | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setCw(entries[0].contentRect.width));
    ro.observe(el);
    setCw(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const { cols, gap, rowH } = GRID;
  const gridFloor = cols * COL_FLOOR + (cols - 1) * gap;
  // A contained horizontal scroller would keep the 656px grid, but the board
  // is tall: scrolling down would hide the widgets that sit to the right.
  // Stacking in reading order (row, then column) keeps every widget full
  // width. Drag and resize stay on the grid only, so a phone gesture cannot
  // rewrite the shared desktop layout.
  const stacked = cw < gridFloor;
  const colW = Math.max(COL_FLOOR, (cw - gap * (cols - 1)) / cols);
  const geo: Geo = useMemo(
    () => ({
      cellX: (x) => x * (colW + gap),
      cellY: (y) => y * (rowH + gap),
      wPx: (w) => w * colW + (w - 1) * gap,
      hPx: (h) => h * rowH + (h - 1) * gap,
      dragging: drag ? drag.id : null,
    }),
    [colW, gap, rowH, drag],
  );

  const maxRow = Math.max(10, ...widgets.map((w) => w.pos.y + w.pos.h));
  const boardH = maxRow * (rowH + gap);

  const startDrag = (e: ReactPointerEvent, widget: Widget) => {
    e.preventDefault();
    setDrag({ id: widget.id, mode: "move", sx: e.clientX, sy: e.clientY, orig: { ...widget.pos } });
  };
  const startResize = (e: ReactPointerEvent, widget: Widget) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ id: widget.id, mode: "resize", sx: e.clientX, sy: e.clientY, orig: { ...widget.pos } });
  };

  // useLayoutEffect, not useEffect: the clear and the listener drop both
  // happen before paint. A passive effect leaves one frame where a pointer
  // move still writes the shared layout.
  useLayoutEffect(() => {
    if (stacked) setDrag(null);
  }, [stacked]);

  useEffect(() => {
    if (!drag || stacked) return;
    const unit = colW + gap;
    const unitY = rowH + gap;
    const move = (e: PointerEvent) => {
      const dx = Math.round((e.clientX - drag.sx) / unit);
      const dy = Math.round((e.clientY - drag.sy) / unitY);
      setWidgets((ws) =>
        ws.map((w) => {
          if (w.id !== drag.id) return w;
          const o = drag.orig;
          if (drag.mode === "move") {
            const x = Math.max(0, Math.min(cols - o.w, o.x + dx));
            const y = Math.max(0, o.y + dy);
            return { ...w, pos: { ...w.pos, x, y } };
          }
          const wd = Math.max(2, Math.min(cols - o.x, o.w + dx));
          const ht = Math.max(2, o.h + dy);
          return { ...w, pos: { ...w.pos, w: wd, h: ht } };
        }),
      );
    };
    const up = () => {
      setDrag(null);
      persist();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, stacked, colW, gap, rowH, cols, setWidgets, persist]);

  const ordered = stacked
    ? [...widgets].sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x || a.id.localeCompare(b.id))
    : widgets;

  return (
    <div
      ref={ref}
      className="rp-board"
      data-layout={stacked ? "stack" : "grid"}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        ...(stacked
          ? {
              display: "flex",
              flexDirection: "column",
              gap: 16,
              height: "auto",
              minHeight: 0,
            }
          : {
              height: boardH,
              minHeight: 400,
              backgroundImage: editMode
                ? `linear-gradient(${C.rule} 1px, transparent 1px), linear-gradient(90deg, ${C.rule} 1px, transparent 1px)`
                : "none",
              backgroundSize: editMode ? `${colW + gap}px ${rowH + gap}px, ${colW + gap}px ${rowH + gap}px` : "auto",
              backgroundPosition: "0 0",
              transition: "background .2s",
            }),
      }}
    >
      {ordered.map((w) => (
        <WidgetFrame
          key={w.id}
          widget={w}
          geo={geo}
          editMode={editMode}
          tickets={tickets}
          prevTickets={prevTickets}
          days={days}
          onOpen={onOpen}
          onEdit={onEdit}
          onDrag={startDrag}
          onResize={startResize}
          stacked={stacked}
          onMutate={(id, fn) => {
            setWidgets((ws) => {
              if (fn === "del") return ws.filter((x) => x.id !== id);
              if (fn === "dup") {
                const src = ws.find((x) => x.id === id);
                if (!src) return ws;
                return [
                  ...ws,
                  { ...src, id: "w" + Date.now().toString(36), pos: { ...src.pos, y: src.pos.y + src.pos.h } },
                ];
              }
              return ws.map((x) => (x.id === id ? fn(x) : x));
            });
            setTimeout(persist, 0);
          }}
        />
      ))}
    </div>
  );
}
